/* eslint-disable import/order */
import React from 'react';
import fetch from 'isomorphic-fetch';
import _get from 'lodash/get';
import _includes from 'lodash/includes';
import { Route, IndexRoute, IndexRedirect, browserHistory, Redirect } from 'react-router';
import { trackOnboardingCompleted, onboardTrackingConstants } from '@zola-helpers/client';
import { ROUTES_PLP_LEAD_CARD } from 'cards/constants/CardTypes';

// Routes
import getWeddingManageRoutes from './routes/weddingManageRoutes';

// Components
import App from './App';
import NotFoundv2 from './pages/NotFoundv2';

import WeddingPartyV2 from './components/PublicWebsite/pages/WeddingPartyV2';
import PoiV2 from './components/PublicWebsite/pages/PoiV2';
import Faq from './components/PublicWebsite/pages/Faq';
import Registry from './components/PublicWebsite/pages/Registry';
import Photos from './components/PublicWebsite/pages/Photos';
import Travel from './components/PublicWebsite/pages/Travel';
import Events from './components/PublicWebsite/pages/Events';
import Home from './components/PublicWebsite/pages/Home';
import Credits from './components/Credits';
import RsvpBase from './components/PublicWebsite/pages/RsvpBase';

import PreAuthApp from './pages/preauth/PreAuthApp';
import SignupIntent from './pages/preauth/SignupIntentPage';
import LoginV2 from './pages/LoginV2';

// settings
import UrlContainer from './pages/settings/url/UrlContainer';
import WeddingDetailsContainer from './pages/settings/wedding/WeddingDetailsContainer';
import PrivacySettings from './pages/settings/privacy/PrivacySettings';
import OtherRegistriesContainer from './pages/settings/otherRegistries/OtherRegistriesContainer';

// Actions
import { submitOnboarding } from './actions/OnboardActions';
import {
  getWeddingBySlug,
  getWeddingPreviewBySlug,
  getWeddingByHashId,
  getRsvpByGuestGroupUuid,
  storeMobileToken,
} from './actions/PublicWebsiteActions';
import { getUserContext } from './actions/UserActions';
import { fetchWeddingAccount } from './actions/WeddingActions';

// card actions
import {
  updateSearchFilters as updateCatalogSearchFilters,
  updateCatalogAdminView,
  getCollaborators,
} from './cards/actions/cardCatalogActions';
import { actions as projectActions } from './cards/actions/projectActions';
import { customizationCleanUp } from './cards/actions/cardCustomizationActions';
import { setActiveSuite } from './cards/reducers/pdp/actions';

// Albums actions
import { setActiveProjectsListing } from 'photobooks/reducers/draftsAndOrders/actions';
import { fetchPhotobookProject, clearPhotobookProject } from 'photobooks/reducers/project/actions';
import { clearAlbum } from 'photobooks/reducers/albumItem/actions';
import { clearGallery } from 'photobooks/reducers/gallery/actions';

// Selectors
import { getProjectUUID, getOrderedSteps } from './cards/selectors/cardProjectSelector';
import {
  getUserHasCompletedOnboarding,
  getUserHasWeddingAccount,
  getUserContext as getUserContextSelector,
} from './selectors/user/userSelectors';

// utils & helpers
import FeatureFlags from './util/featureFlags';
import PrintChecklistContainer from './components/manage/ChecklistV2/PrintChecklist/PrintChecklistContainer';
import flags, {
  SELECT_PRODUCT_NEW_QS,
  WEDDING_WEBSITE_NEW_QS,
  WEDDING_CHECKLIST_NEW_QS,
} from './util/experimentFlags';
import { getAlbumInfoFromUrl } from 'photobooks/utils/albumUtils';

import { getStepUrl } from './cards/util/getStepUrl';
import { getStepFromPathname } from './cards/util/customization';
import isVirtualEvents from './components/manage/VirtualEvents/util/isVirtualEvents';

// call updateBoxShadow (which is set in web-nav) becuz if you visit a weddings page with no sub-nav (ie, web-nav has a dropshadow),
// and then navigate to a weddings page with a sub-nav, it should update web-nav to NOT have a dropshadow (and vice versa);
browserHistory.listen(location => {
  if (location.pathname) {
    if (window.zolaNav && window.zolaNav.updateBoxShadow) {
      window.zolaNav.updateBoxShadow(location.pathname);
    }
  }
});

const { BUSINESS_UNIT, COMPONENT } = onboardTrackingConstants;

const getRoutes = store => {
  const checkWeddingPreviewBySlug = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { slug } = nextState.params;
    dispatch(getWeddingPreviewBySlug(slug))
      .then(() => {
        const state = store.getState();
        if (state.publicWebsite.error) {
          replaceState('/notfound');
        }
        callback();
      })
      .catch(() => {
        replaceState('/notfound');
        callback();
      });
  };

  const checkGetUser = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getUserContext())
      .then(() => callback())
      .catch(() => callback());
  };

  const checkWeddingByHashId = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { hashId } = nextState.params;
    dispatch(getWeddingByHashId(hashId))
      .then(() => {
        const state = store.getState();
        if (state.publicWebsite.error) {
          replaceState('/notfound');
        }
        callback();
      })
      .catch(() => {
        replaceState('/notfound');
        callback();
      });
  };

  const checkWeddingWithToken = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { slug } = nextState.params;
    const token = nextState.location.query.preview_token;
    if (token) {
      dispatch(storeMobileToken(token));
      fetch('/website-nav/web-api/v1/auth/mobile-website-preview', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      })
        .then(() => {
          dispatch(getWeddingBySlug(slug))
            .then(() => {
              callback();
            })
            .catch(() => {
              replaceState('/notfound');
              callback();
            });
        })
        .catch(() => {
          replaceState('/notfound');
          callback();
        });
    } else {
      dispatch(getWeddingBySlug(slug))
        .then(() => {
          callback();
        })
        .catch(e => {
          if (e.response && e.response.status === 403) {
            replaceState(`/wedding/${slug}/passcode`);
          } else {
            replaceState('/notfound');
          }
          callback();
        });
    }
  };

  const checkWeddingSlug = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { slug } = nextState.params;

    dispatch(getWeddingBySlug(slug))
      .then(() => {
        replaceState(`/wedding/${slug}`);
        callback();
      })
      .catch(e => {
        if (e.response && !(e.response.status === 403)) {
          replaceState('/notfound');
        }
        callback();
      });
  };

  // Confirm a user is not logged in before displaying pre-auth onboarding routes.
  const confirmPreAuth = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getUserContext())
      .then(() => {
        const state = store.getState();
        if (state.user.userContext && !state.user.userContext.is_guest) {
          window.location.href = '/';
          return;
        }
        callback();
      })
      .catch(() => {
        window.location.href = '/';
      });
  };

  const checkLoginAndRedirectToReferralPage = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getUserContext())
      .then(() => {
        const state = store.getState();
        // if user is guest or user is "idle" (been logged in for more than 30 minutes), then redirect them to login page
        if (
          !state.user.userContext ||
          state.user.userContext.is_guest ||
          state.user.userContext.status === 'IDLE'
        ) {
          browserHistory.push(`/account/login?returnTo=${window.location.pathname}`);
        } else if (
          state.user.userContext.has_wedding_account ||
          state.user.userContext.has_registry
        ) {
          callback();
        }
        callback();
      })
      .catch(() => {
        replaceState('/account/login');
        callback();
      });
  };

  // checks for a specific requirement and if it doesn't exist, redirects them to another page
  const checkForRequirement = requirement => (nextState, replaceState, callback) => {
    const state = store.getState();
    const { slug } = state.user.userContext.user_role_account_weddings[0];
    const registry = state.user.userContext.has_registry;
    const wedding = state.user.userContext.has_wedding_account;
    const onboarding = state.user.userContext.has_completed_onboarding;

    if (requirement === 'slug' && !slug) {
      window.location = '/account/settings/info';
    } else if (requirement === 'registryOrWedding' && !registry && !wedding) {
      window.location = '/account/settings/info';
    } else if (requirement === 'onboarding' && !onboarding) {
      window.location = '/wedding/onboard';
    } else {
      // if doesn't meet the requirements, then call `callback` (aka send them back to the original page);
      callback();
    }
  };

  // Auto onboard products that only requires owner name, partner name and wedding date
  const checkOnboardStatus = (nextState, replaceState, callback) => {
    const { dispatch, getState } = store;
    const state = getState();
    const userContext = getUserContextSelector(state);
    let { product } = nextState.location.query;
    const { pathname } = nextState.location;
    const NEW_PLANNING_PATH = '/wedding/onboard/wedding-planning';
    if (pathname === NEW_PLANNING_PATH) {
      product = 'WEDDING_PLANNING';
    }
    const NEW_SELECT_PRODUCT_PATH = '/wedding/onboard/select-product';
    if (pathname === NEW_SELECT_PRODUCT_PATH) {
      product = 'SELECT_PRODUCT';
    }
    const NEW_WEDDING_CHECKLIST_PATH = '/wedding/onboard/wedding-checklist';
    if (pathname === NEW_WEDDING_CHECKLIST_PATH) {
      product = 'WEDDING_CHECKLIST';
    }
    const NEW_WEDDING_WEBSITE_PATH = '/wedding/onboard/wedding-website';
    if (pathname === NEW_WEDDING_WEBSITE_PATH) {
      product = 'WEDDING_WEBSITE';
    }

    // Weddings products with basic onboarding flows
    const basicFlowWeddingProducts = {
      [COMPONENT.WEDDING_PLANNING]: '/wedding/manage',
      [COMPONENT.WEDDING_GUEST_LIST]: '/wedding/manage/guests/all',
      [COMPONENT.WEDDING_WEBSITE]: '/wedding/manage/website/designs',
    };
    // only auto onboard existing users with wedding details
    if (!getUserHasCompletedOnboarding(state)) {
      // Check if the user should be placed in the new flow experiment
      // Only new/basic wedding planning flow users should be bucketed in the experiment
      if (product === COMPONENT.WEDDING_PLANNING && pathname !== NEW_PLANNING_PATH) {
        replaceState('/wedding/onboard/wedding-planning');
      }

      const isSelectProductFlagActive = flags.getFlag(SELECT_PRODUCT_NEW_QS, userContext);
      if (
        product === 'SELECT_PRODUCT' &&
        pathname !== NEW_SELECT_PRODUCT_PATH &&
        isSelectProductFlagActive
      ) {
        replaceState('/wedding/onboard/select-product');
      }

      const isNewChecklistFlowFlagActive = flags.getFlag(WEDDING_CHECKLIST_NEW_QS, userContext);
      if (
        product === 'WEDDING_CHECKLIST' &&
        pathname !== NEW_WEDDING_CHECKLIST_PATH &&
        isNewChecklistFlowFlagActive
      ) {
        replaceState(NEW_WEDDING_CHECKLIST_PATH);
      }
      const isNewWebsiteFlowFlagActive = flags.getFlag(WEDDING_WEBSITE_NEW_QS, userContext);
      if (
        product === 'WEDDING_WEBSITE' &&
        pathname !== NEW_WEDDING_WEBSITE_PATH &&
        isNewWebsiteFlowFlagActive
      ) {
        replaceState(NEW_WEDDING_WEBSITE_PATH);
      }
      callback();
      return;
    }

    // onboard users automatically for wedding planning
    if (Object.keys(basicFlowWeddingProducts).includes(product)) {
      if (getUserHasWeddingAccount(state)) {
        replaceState(basicFlowWeddingProducts[product]);
        callback();
      } else {
        // create a wedding account
        const onboardingData = {
          existing_account: true,
          business_unit: product,
          answers: [],
        };
        dispatch(submitOnboarding(onboardingData)).then(() => {
          trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, product, { isExistingUser: true });
          trackOnboardingCompleted(BUSINESS_UNIT.WEDDINGS, COMPONENT.WEDDINGS, {
            isExistingUser: true,
          });
          replaceState(basicFlowWeddingProducts[product]);
          callback();
        });
      }
      return;
    }

    // User has onboarded but is trying to access the SELECT_PRODUCT onboard for some reason
    if (product === 'SELECT_PRODUCT') {
      replaceState('/onboard/select-product');
    }

    // TODO: this flow needs to be updated to handle existing users.
    // Onboarded but is trying to access the new WEDDING_CHECKLIST flow
    if (product === 'WEDDING_CHECKLIST' && pathname === NEW_WEDDING_CHECKLIST_PATH) {
      replaceState('/wedding/onboard?product=WEDDING_CHECKLIST');
    }

    callback();
  };

  // stops a user with a wedding account from seeing /wedding/designs.
  const checkWedding = ({ redirectTo }) => (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getUserContext())
      .then(() => {
        const state = store.getState();
        if (state.user.userContext.has_wedding_account) {
          window.location = redirectTo;
        } else {
          callback();
        }
      })
      .catch(() => {
        callback();
      });
  };

  const checkGuestByUUID = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const nextParams = nextState.params;
    const { slug, uuid } = nextParams;
    if (uuid) {
      dispatch(getRsvpByGuestGroupUuid(uuid))
        .then(() => {
          callback();
        })
        .catch(() => {
          replaceState(`/wedding/${slug}/rsvp`);
          callback();
        });
    } else {
      replaceState(`/wedding/${slug}/rsvp`);
      callback();
    }
  };

  // onEnter hook used for customization flow
  const onEnterCustomizationFlow = (nextState, replaceState, callback) => {
    const { projectUUID } = nextState.params;
    const { dispatch } = store;
    const state = store.getState();
    const {
      userContext: {
        has_wedding_account: hasWeddingAccount,
        has_invitation_account: hasInvitationAccount,
        invitation_account: { cards_onboarding_completed: isCardOnboardingCompleted } = {},
      } = {},
    } = state.user;
    const hasCardOnboardingCompleted = FeatureFlags.get('albumsCore')
      ? isCardOnboardingCompleted
      : true;
    // Some cleanup to restore reducer state for errors and customization toggles
    dispatch(customizationCleanUp());
    if (state.user.hasIdentified && hasInvitationAccount && hasCardOnboardingCompleted) {
      if (hasWeddingAccount) {
        dispatch(fetchWeddingAccount());
      }
      dispatch(projectActions.getProjectAuthenticated(projectUUID)).then(ownsProject => {
        if (!ownsProject) {
          replaceState('/wedding-planning/');
          callback();
        }
        if (getProjectUUID(state) === projectUUID) {
          // eslint-disable-line
          // If the project is already in the store, continue.
          callback();
        } else {
          // Otherwise, load the project (and all customization variants)
          dispatch(projectActions.fetchProject(projectUUID)).then(project => {
            const { completed_at: completed } = project;
            if (completed) {
              replaceState('/wedding-planning/projects/finished');
            }
            callback();
          });
        }
      });
    } else {
      window.location.href = '/wedding-planning/paper';
      callback();
    }
  };

  const onEnterAlbumsCustomizationStep = (nextState, replaceState, callback) => {
    const state = store.getState();
    const {
      userContext: {
        has_invitation_account: hasInvitationAccount,
        invitation_account: {
          photobooks_onboarding_completed: isPhotobookOnboardingCompleted,
        } = {},
      } = {},
    } = state.user;
    const { dispatch } = store;

    // Login check
    if (state.user.hasIdentified && hasInvitationAccount && isPhotobookOnboardingCompleted) {
      // Project ownership check
      const { projectUuid } = getAlbumInfoFromUrl();
      dispatch(fetchPhotobookProject(projectUuid))
        .then(data => {
          if (!data) {
            replaceState('/wedding-albums/shop');
            callback();
          }
          callback();
        })
        .catch(() => {
          replaceState('/wedding-albums/shop');
          callback();
        });
    } else {
      replaceState('/wedding-albums/shop');
      callback();
    }
  };

  const onLeaveAlbumsCustomizationStep = () => {
    const { dispatch } = store;
    dispatch(clearPhotobookProject());
    dispatch(clearAlbum());
    dispatch(clearGallery());
  };

  const onEnterPublicDigitalPreview = (nextState, replaceState, callback) => {
    const { projectUUID } = nextState.params;
    const { dispatch } = store;
    const state = store.getState();
    // Some cleanup to restore reducer state for errors and customization toggles
    dispatch(customizationCleanUp());
    if (state.user.hasIdentified && state.user.userContext.has_invitation_account) {
      dispatch(fetchWeddingAccount());
      dispatch(projectActions.getProjectAuthenticated(projectUUID)).then(ownsProject => {
        if (!ownsProject) {
          replaceState('/wedding-planning/');
          callback();
        }
        if (getProjectUUID(state) === projectUUID) {
          // eslint-disable-line
          callback();
        } else {
          dispatch(projectActions.fetchProject(projectUUID)).then(() => {
            callback();
          });
        }
      });
    } else {
      window.location.href = '/wedding-planning/paper';
      callback();
    }
  };

  // onEnter hook used for customization steps
  const onEnterCustomizationStep = (nextState, replaceState, callback) => {
    const { projectUUID } = nextState.params;
    const state = store.getState();
    const orderedSteps = getOrderedSteps(state);
    const currentStep = getStepFromPathname(nextState.location.pathname, orderedSteps);

    if (currentStep) {
      callback();
    } else {
      // If specified step is unsupported, send to the first step
      replaceState(getStepUrl(projectUUID));
      callback();
    }
  };

  const onEnterPublicDigital = (nextState, replaceState, callback) => {
    const { uuid } = nextState.params;
    const { dispatch } = store;
    dispatch(projectActions.requestPublicCard(uuid)).then(() => callback());
  };

  // onEnter hook for viewing digital preview
  const onEnterDigitalPreview = (nextState, replaceState, callback) => {
    const { projectUUID } = nextState.params;
    const { dispatch } = store;
    const state = store.getState();
    if (state.user.hasIdentified && state.user.userContext.has_invitation_account) {
      dispatch(fetchWeddingAccount());
      dispatch(projectActions.getProjectAuthenticated(projectUUID)).then(ownsProject => {
        if (!ownsProject) {
          replaceState('/wedding-planning/');
          callback();
        }
        if (getProjectUUID(state) === projectUUID) {
          // eslint-disable-line
          callback();
        } else {
          dispatch(projectActions.fetchProject(projectUUID)).then(() => {
            callback();
          });
        }
      });
    } else {
      window.location.href = '/wedding-planning/paper';
      callback();
    }
  };

  const onEnterDigitalComplete = (nextState, replaceState, callback) => {
    const { projectUUID } = nextState.params;
    const { dispatch } = store;
    const state = store.getState();
    if (getProjectUUID(state) === projectUUID) {
      callback();
    } else {
      dispatch(projectActions.fetchProject(projectUUID)).then(() => callback());
    }
  };

  const gotoOrder = ({ redirectTo }) => (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(getUserContext())
      .then(() => {
        const state = store.getState();
        if (
          !state.user.userContext ||
          state.user.userContext.is_guest ||
          !state.user.userContext.has_invitation_account
        ) {
          replaceState(redirectTo);
        }
        callback();
      })
      .catch(() => {
        replaceState('/notfound');
        callback();
      });
  };
  // TODO: Some of this methods are duplicates, will need to review later to remove and unify
  const onCheckCardType = (nextState, replaceState, callback) => {
    const { cardType } = nextState.params;
    const allowedCardTypes = [
      'save-the-date',
      'invitations',
      'thank-you-cards',
      'programs',
      'menus',
      'enclosures',
      'holiday-cards',
    ];

    if (FeatureFlags.get('changeTheDateCardType')) {
      allowedCardTypes.push('change-the-date');
    }

    if (!_includes(allowedCardTypes, cardType)) {
      window.location.href = '/wedding-planning/paper';
    }
    callback();
  };

  const onCheckAdminCardType = (nextState, replaceState, callback) => {
    const { cardType } = nextState.params;
    const allowedCardTypes = [
      'save-the-date',
      'change-the-date',
      'invitations',
      'thank-you-cards',
      'programs',
      'menus',
      'enclosures',
      'holiday-cards',
    ];

    if (!_includes(allowedCardTypes, cardType)) {
      window.location.href = '/wedding-planning/paper';
    }
    callback();
  };

  const onEnterPLP = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { cardType } = nextState.params;
    const { leadCardType, collaborator } = ROUTES_PLP_LEAD_CARD[cardType];

    dispatch(getCollaborators());
    dispatch(updateCatalogSearchFilters('lead_card_type', leadCardType));
    if (collaborator) {
      dispatch(updateCatalogSearchFilters('collaborator', collaborator));
    }
    dispatch(updateCatalogSearchFilters('active_only', true));
    dispatch(updateCatalogAdminView(false));

    callback();
  };

  const onEnterAdminPLP = (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { cardType } = nextState.params;
    const { leadCardType, collaborator } = ROUTES_PLP_LEAD_CARD[cardType];

    dispatch(getCollaborators());
    dispatch(updateCatalogSearchFilters('lead_card_type', leadCardType));
    if (collaborator) {
      dispatch(updateCatalogSearchFilters('collaborator', collaborator));
    }
    dispatch(updateCatalogSearchFilters('active_only', false));
    dispatch(updateCatalogAdminView(true));

    callback();
  };

  const onEnterPhotobookProjects = activeView => (nextState, replaceState, callback) => {
    const { dispatch } = store;
    dispatch(setActiveProjectsListing(activeView));
    const state = store.getState();
    const { userContext } = state.user;
    if (!userContext || userContext.is_guest || !userContext.has_invitation_account) {
      replaceState('/wedding-albums/shop');
      callback();
    }
    callback();
  };

  const enterPDP = isAdminView => (nextState, replaceState, callback) => {
    const { dispatch } = store;
    const { uuid, cardType } = nextState.params;
    dispatch(updateCatalogAdminView(isAdminView));
    dispatch(setActiveSuite(uuid));
    try {
      callback();
    } catch (e) {
      replaceState(`/wedding-planning/${cardType}/shop`);
    }
  };
  const onEnterPDP = enterPDP(false);
  const onEnterAdminPDP = enterPDP(true);

  const checkVirtualEventsFlag = (nextState, replaceState, callback) => {
    const state = store.getState();
    const { userContext } = state.user;
    if (isVirtualEvents(userContext)) {
      callback();
    } else {
      window.location.href = '/';
    }
  };

  return (
    <Route component={App} onEnter={checkGetUser}>
      <Route
        path="addr/:hashId"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('./pages/collectInfo/CollectInfo').default);
            },
            'CollectInfo'
          );
        }}
        onEnter={checkWeddingByHashId}
      />
      <Route
        path="address/:slug"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('./pages/collectInfo/CollectInfo').default);
            },
            'CollectInfo'
          );
        }}
        onEnter={checkWeddingPreviewBySlug}
      />
      {FeatureFlags.get('albumsCore') && (
        <Route
          path="/wedding-albums"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./photobooks/pages/Photobooks').default);
              },
              'Photobooks'
            );
          }}
        >
          <IndexRedirect to="/wedding-albums/shop" />
          <Route
            path="shop"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./photobooks/pages/plp/AlbumListingPage').default);
                },
                'AlbumListingPage'
              );
            }}
          />
          <Route
            path="select"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./photobooks/pages/pdp/AlbumDetailsPage').default);
                },
                'AlbumDetailsPage'
              );
            }}
          />
          <Route
            path="create"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  const Component = require('./photobooks/pages/customization/AlbumCustomizationFlowPage')
                    .default;
                  callback(null, props => <Component key={location.pathname} {...props} />);
                },
                'AlbumCustomizationFlowPage'
              );
            }}
          >
            <IndexRedirect to="/wedding-albums/shop" />
            <Route
              path="layout"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(
                      null,
                      require('./photobooks/pages/customization/steps/LayoutStep').default
                    );
                  },
                  'LayoutStep'
                );
              }}
            />
            <Route
              path="upload"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(
                      null,
                      require('./photobooks/pages/customization/steps/UploadStep').default
                    );
                  },
                  'UploadStep'
                );
              }}
            />
            <Route
              path=":projectUuid/:albumUuid"
              onEnter={onEnterAlbumsCustomizationStep}
              onLeave={onLeaveAlbumsCustomizationStep}
            >
              <Route
                path="organize"
                getComponent={(location, callback) => {
                  require.ensure(
                    [],
                    require => {
                      callback(
                        null,
                        require('./photobooks/pages/customization/steps/OrganizeStep').default
                      );
                    },
                    'OrganizeStep'
                  );
                }}
              />
              <Route
                path="highlight"
                getComponent={(location, callback) => {
                  require.ensure(
                    [],
                    require => {
                      callback(
                        null,
                        require('./photobooks/pages/customization/steps/HighlightStep').default
                      );
                    },
                    'HighlightStep'
                  );
                }}
              />
              <Route
                path="customize"
                getComponent={(location, callback) => {
                  require.ensure(
                    [],
                    require => {
                      callback(
                        null,
                        require('./photobooks/pages/customization/steps/CustomizeStep').default
                      );
                    },
                    'CustomizeStep'
                  );
                }}
              />
              <Route
                path="cover"
                getComponent={(location, callback) => {
                  require.ensure(
                    [],
                    require => {
                      callback(
                        null,
                        require('./photobooks/pages/customization/steps/CoverStep').default
                      );
                    },
                    'CoverStep'
                  );
                }}
              />
            </Route>
          </Route>
          <Route
            path="drafts"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./photobooks/pages/draftsAndOrders/DraftsAndOrdersPage').default
                  );
                },
                'AlbumDrafts'
              );
            }}
            onEnter={onEnterPhotobookProjects('drafts')}
          />
          <Route
            path="orders"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./photobooks/pages/draftsAndOrders/DraftsAndOrdersPage').default
                  );
                },
                'AlbumOrders'
              );
            }}
            onEnter={onEnterPhotobookProjects('orders')}
          />
        </Route>
      )}
      {/* WEDDING  */}
      <Route path="/wedding-planning" onEnter={checkGetUser} component={PreAuthApp}>
        {_includes(['development', 'staging'], _get(window, 'zola.env')) && ( // Generic route for sandbox work.
          <Route
            path="sandbox"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./components/onboard/Stories').default);
                },
                'Stories'
              );
            }}
          />
        )}
        <Route
          path="website/designs"
          onEnter={checkWedding({ redirectTo: '/wedding/manage/website/designs' })}
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/preauth/ThemesContainerV2').default);
              },
              'ThemesContainerV2'
            );
          }}
        />
        <Route
          path="website/design/:themeKey"
          onEnter={checkWedding({ redirectTo: '/wedding/manage/website/designs' })}
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/preauth/ThemeDetail').default);
              },
              'ThemeDetail'
            );
          }}
        />
      </Route>
      {/* END --- WEDDING */}

      <Route
        path="/wedding-planning"
        getComponent={(location, callback) => {
          require.ensure(
            [],
            require => {
              callback(null, require('./cards/pages/Cards').default); // Cards
            },
            'Cards'
          );
        }}
      >
        <Route path="/digital">
          <IndexRedirect to="/" />
          <Route
            path=":uuid"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./cards/pages/PublicDigitalCard').default);
                },
                'PublicDigitalCard'
              );
            }}
            onEnter={onEnterPublicDigital}
          />
        </Route>
        <Route
          path="digitalPreview/:projectUUID"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./cards/pages/DigitalPreviewPage').default);
              },
              'DigitalPreviewPage'
            );
          }}
          onEnter={onEnterDigitalPreview}
        />
        <Route
          path="digitalOrder/:projectUUID"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./cards/pages/DigitalPreviewPage').default);
              },
              'DigitalPreviewPage'
            );
          }}
          onEnter={onEnterPublicDigitalPreview}
        />
        {
          // digital checkout confirmation
          FeatureFlags.get('digitalStageTwo') && (
            <Route
              path="digitalComplete/:projectUUID"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(
                      null,
                      require('./cards/components/Customization/digitalSteps/DigitalCompletion/DigitalCompletion')
                        .default
                    );
                  },
                  'DigitalCompletion'
                );
              }}
              onEnter={onEnterDigitalComplete}
            />
          )
        }
        <Route
          path="projects"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./cards/pages/CardProjects_v2').default);
              },
              'CardProjects'
            );
          }}
        >
          <IndexRedirect to="drafts" />
          <Route
            path="drafts"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/ProjectDrafts/ProjectDrafts_v2').default
                  );
                },
                'ProjectDrafts'
              );
            }}
            onEnter={gotoOrder({ redirectTo: '/wedding-planning/save-the-date/shop' })}
          />
          <Route
            path="finished"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/ProjectOrders/ProjectOrders_v2').default
                  );
                },
                'ProjectOrders'
              );
            }}
            onEnter={gotoOrder({ redirectTo: '/wedding-planning/save-the-date/shop' })}
          />
        </Route>
        {/* Customization flow routes */}
        <Route
          path="custom/:projectUUID"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./cards/pages/CardCustomizationFlow').default);
              },
              'CardCustomizationFlowV2'
            );
          }}
          onEnter={onEnterCustomizationFlow}
        >
          <Route
            path="digital"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/digitalSteps/DigitalCard').default
                  );
                },
                'DigitalCard'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="guests"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Addressing/AddressingV2')
                      .default
                  );
                },
                'Guests'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="review"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/digitalSteps/DigitalReview/DigitalReview')
                      .default
                  );
                },
                'DigitalReview'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="detailFront"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/EventDetailFront').default
                  );
                },
                'EventDetailFrontV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="detailInside"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  const step = require('./cards/components/Customization/steps/EventDetailInside');
                  callback(null, step.default);
                },
                'EventDetailInsideV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="detailWrite"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/EventDetailWrite').default
                  );
                },
                'EventDetailWrite'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="detailBack"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/EventDetailBack').default
                  );
                },
                'EventDetailBackV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="rsvp"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./cards/components/Customization/steps/RSVP').default);
                },
                'RSVPV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="rsvpEnvelope"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/RSVPEnvelope').default
                  );
                },
                'RsvpEnvelopeV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="envelope"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Envelope').default
                  );
                },
                'EnvelopeV2'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="recipients"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Recipients/Recipients').default
                  );
                },
                'Recipients'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="addressing"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Addressing/Addressing').default
                  );
                },
                'Addressing'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="addressingPreview"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Addressing/Addressing').default
                  );
                },
                'Addressing'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          <Route
            path="reviewPaper"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./cards/components/Customization/steps/Review/Review').default
                  );
                },
                'Review'
              );
            }}
            onEnter={onEnterCustomizationStep}
          />
          {FeatureFlags.get('enclosuresInInvitesFlow') && (
            <Route path=":cardTypeSlug">
              <Route
                path="detailFront"
                getComponent={({ location }, callback) => {
                  require.ensure(
                    [],
                    require => {
                      const Component = require('./cards/components/Customization/steps/EventDetailFront')
                        .default;
                      callback(null, props => <Component key={location.pathname} {...props} />); // The `key` prop makes the component remount on URL change
                    },
                    'EventDetailFrontV2'
                  );
                }}
                onEnter={onEnterCustomizationStep}
              />
              <Route
                path="detailInside"
                getComponent={({ location }, callback) => {
                  require.ensure(
                    [],
                    require => {
                      const step = require('./cards/components/Customization/steps/EventDetailInside');
                      const Component = step.default;
                      callback(null, props => <Component key={location.pathname} {...props} />); // The `key` prop makes the component remount on URL change
                    },
                    'EventDetailInsideV2'
                  );
                }}
                onEnter={onEnterCustomizationStep}
              />
              <Route
                path="detailWrite"
                getComponent={(location, callback) => {
                  require.ensure(
                    [],
                    require => {
                      const Component = require('./cards/components/Customization/steps/EventDetailWrite')
                        .default;
                      callback(null, props => <Component key={location.pathname} {...props} />); // The `key` prop makes the component remount on URL change
                    },
                    'EventDetailWrite'
                  );
                }}
                onEnter={onEnterCustomizationStep}
              />
              <Route
                path="detailBack"
                getComponent={({ location }, callback) => {
                  require.ensure(
                    [],
                    require => {
                      const Component = require('./cards/components/Customization/steps/EventDetailBack')
                        .default;
                      callback(null, props => <Component key={location.pathname} {...props} />); // The `key` prop makes the component remount on URL change
                    },
                    'EventDetailBackV2'
                  );
                }}
                onEnter={onEnterCustomizationStep}
              />
            </Route>
          )}
        </Route>

        {/* Admin PLP and PDP routes */}
        <Route path="admin">
          <Route path=":cardType" onEnter={onCheckAdminCardType}>
            <IndexRedirect to="./" />
            <Route
              path="shop"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(null, require('./cards/pages/CardListingPage').default); // Invitation PLP
                  },
                  'CardListingPage'
                );
              }}
              onEnter={onEnterAdminPLP}
            />
            <Route
              path=":uuid"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(null, require('./cards/pages/CardDetailPage').default);
                  },
                  'CardDetailPage'
                );
              }}
              onEnter={onEnterAdminPDP}
            />
          </Route>
        </Route>

        {/* PLP and PDP routes */}
        <Route path=":cardType" onEnter={onCheckCardType}>
          <IndexRedirect to="./" />
          <Route
            path="shop"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./cards/pages/CardListingPage').default); // Invitation PLP
                },
                'CardListingPage'
              );
            }}
            onEnter={onEnterPLP}
          />
          <Route
            path=":uuid"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./cards/pages/CardDetailPage').default);
                },
                'CardDetailPage'
              );
            }}
            onEnter={onEnterPDP}
          />
        </Route>
      </Route>

      {/* Preauth onboarding pages */}
      <Route path="/onboard">
        <Route path="login" component={LoginV2} onEnter={confirmPreAuth} />
        <Route path="select-product" component={SignupIntent} />
        <IndexRedirect to="/onboard/select-product" />
        <Route path="*" component={NotFoundv2} />
      </Route>
      <Route path="/wedding" onEnter={checkGetUser}>
        <IndexRedirect to="/wedding-planning" />
        <Route
          path="mobile-landing"
          component={() => {
            window.location.href = '/onboard/new';
            return null;
          }}
        />
        <Route
          path="guest-help"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/GuestHelp').default);
              },
              'GuestHelp'
            );
          }}
        />
        <Redirect from="login" to="/account/login" />
        <Route path="onboard" onEnter={checkOnboardStatus}>
          <IndexRoute
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./components/onboard/OnboardContainer').default);
                },
                'OnboardContainer'
              );
            }}
          />
          <Route
            path="wedding-planning"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./components/onboard/flowControllers/WeddingPlanningFlow').default
                  );
                },
                'WeddingPlanningOnboardFlow'
              );
            }}
          />
          <Route
            path="select-product"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./components/onboard/flowControllers/SelectProductFlow').default
                  );
                },
                'SelectProductOnboardFlow'
              );
            }}
          />
          <Route
            path="wedding-website"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./components/onboard/flowControllers/WeddingWebsiteFlow').default
                  );
                },
                'WeddingWebsiteOnboardFlow'
              );
            }}
          />
          <Route
            path="wedding-checklist"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./components/onboard/flowControllers/WeddingChecklistFlow').default
                  );
                },
                'WeddingChecklistOnboardFlow'
              );
            }}
          />
        </Route>
        {/* getWeddingManageRoutes All nested routes of /wedding/manage */}
        {getWeddingManageRoutes(store)}
        <Route path="credits" component={Credits} />
        <Route
          path=":slug/passcode"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/Passcode').default);
              },
              'Passcode'
            );
          }}
          onEnter={checkWeddingSlug}
        />
        <Route
          path=":slug/virtual-embed/:eventHash"
          onEnter={checkWeddingWithToken}
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./components/PublicWebsite/pages/VirtualEmbed').default);
              },
              'VirtualEmbed'
            );
          }}
        />
        <Route
          path=":slug/virtual-embed/:eventHash/meeting-over"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(
                  null,
                  require('./components/PublicWebsite/pages/Virtual/ZoomMeetingOver').default
                );
              },
              'ZoomMeetingOver'
            );
          }}
        />

        <Route
          path=":slug"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/Public').default);
              },
              'Public'
            );
          }}
          onEnter={checkWeddingWithToken}
        >
          <IndexRoute component={Home} />
          <Route path="event" component={Events} />
          <Route path="faq" component={Faq} />
          <Route path="wedding_party" component={WeddingPartyV2} />
          <Route path="registry" component={Registry} />
          <Route path="poi" component={PoiV2} />
          <Route path="photo" component={Photos} />
          <Route path="travel" component={Travel} />
          <Route
            path="rsvp"
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(null, require('./components/PublicWebsite/pages/RsvpBase').default);
                },
                'RsvpBase'
              );
            }}
          >
            <Route path=":uuid" component={RsvpBase} onEnter={checkGuestByUUID} />
          </Route>
          <Route path="virtual" onEnter={checkVirtualEventsFlag}>
            <Route
              path=":eventHash"
              getComponent={(location, callback) => {
                require.ensure(
                  [],
                  require => {
                    callback(null, require('./components/PublicWebsite/pages/Virtual').default);
                  },
                  'Virtual'
                );
              }}
            />
          </Route>
        </Route>
        <Route path="print">
          <Route
            path="checklist"
            component={PrintChecklistContainer}
            getComponent={(location, callback) => {
              require.ensure(
                [],
                require => {
                  callback(
                    null,
                    require('./components/manage/ChecklistV2/PrintChecklist/PrintChecklistContainer')
                      .default
                  );
                },
                'PrintChecklistContainer'
              );
            }}
          />
        </Route>
        <Route path="/notfound" component={NotFoundv2} />
        <Route path="*" component={NotFoundv2} />
      </Route>
      {/* START --- new account pages */}
      <Route exact path="/account/login" component={LoginV2} />
      <Route path="/account" onEnter={checkLoginAndRedirectToReferralPage}>
        <Route
          path="settings"
          getComponent={(location, callback) => {
            require.ensure(
              [],
              require => {
                callback(null, require('./pages/settings/AccountSettings').default);
              },
              'AccountSettings'
            );
          }}
          onEnter={checkGetUser}
        >
          <Route path="urls" component={UrlContainer} onEnter={checkForRequirement('slug')} />
          <Route
            path="wedding"
            component={WeddingDetailsContainer}
            onEnter={checkForRequirement('onboarding')}
          />
          <Route
            path="other-registries"
            component={OtherRegistriesContainer}
            onEnter={checkForRequirement('registry')}
          />
          <Route
            path="privacy"
            component={PrivacySettings}
            onEnter={checkForRequirement('registryOrWedding')}
          />
        </Route>
      </Route>
      {/* EXTERNAL REDIRECTS - there might be a better way to do this */}
      <Route
        path="/registry/dashboard"
        component={() => {
          window.location = '/registry/dashboard';
        }}
      />
    </Route>
  );
};

export default getRoutes;



// WEBPACK FOOTER //
// ./src/routes.jsx